import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Gmap from './Gmap';
import Select, { components } from 'react-select';
import Slider from '@mui/material/Slider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import {
  carModelMakes as carMakeOptions,
  motorcycleModelMakes as motorMakeOptions,
  colorOptions,
  transOptions,
  driveOptions,
  engineOptions,
  bodyOptions,
  fuelOptions,
  premiumFeaturesOptions,
  carModelMakesJson,
} from '../contants/data';
import useToggle from '../hooks/useToggle';
import { GrPowerReset } from 'react-icons/gr';
import { FaPlusCircle, FaMinusCircle } from 'react-icons/fa';

const FiltersMobile = ({ onFilter, data, stateCategory, filters }) => {
  const [thunks, setThunks] = useState(null);
  const [showAdvFilter, setShowAdvFilter] = useState(false);
  const { isFilter } = useSelector((state) => state[stateCategory]);
  const dispatch = useDispatch();

  // GEOLOCATE
  const { lat, lng, radius } = useSelector(
    (state) => state[stateCategory].filters.location
  );

  // NEW LAT,LNG
  const [newLat, setNewLat] = useState('');
  const [newLng, setNewLng] = useState('');

  // MAKE, MODEL
  const { make, model } = useSelector((state) => state[stateCategory].filters);
  const [modelOptions, setModelOptions] = useState([]);
  const [toggleMake, handleToggleMake] = useToggle();
  const [toggleModel, handleToggleModel] = useToggle();

  const [selectedMake, setSelectedMake] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);

  // Helpers
  const uniqBy = (arr, keyFn) => {
    const map = new Map();
    for (const item of arr) map.set(keyFn(item), item);
    return Array.from(map.values());
  };
  const normalizeToArray = (val) =>
    Array.isArray(val) ? val : val ? [val] : [];

  // Build Make options (cars/classic from JSON, motorcycles from constant)
  const makeOptionsFromJson = useMemo(
    () =>
      (carModelMakesJson || []).map((m) => ({ label: m.make, value: m.make })),
    []
  );

  // Build union Model options across all selected makes
  const getModelOptions = (makeVals) => {
    const makeArr = normalizeToArray(makeVals);
    const all = makeArr.flatMap((mk) => {
      const models = carModelMakesJson.find((m) => m.make === mk)?.models || [];
      return models.map((m) => {
        const kids = Array.isArray(m.children) ? m.children : [];
        const tokens = [...new Set([m.model, ...kids])];
        return { label: m.model, value: tokens.join(',') };
      });
    });
    return uniqBy(all, (x) => x.label.toLowerCase());
  };

  const handleRemoveOption = (removedOption) => {
    const updatedMakes = (selectedMake || []).filter(
      (opt) => opt.value !== removedOption.value
    );
    setSelectedMake(updatedMakes);

    // Recompute models from remaining makes (cars/classic)
    if (stateCategory === 'cars' || stateCategory === 'classicCollectorCars') {
      const remainingMakeVals = updatedMakes.map((o) => o.value);
      const nextModelOptions = getModelOptions(remainingMakeVals);
      setModelOptions(nextModelOptions);

      // Prune selectedModel that no longer exist
      setSelectedModel((prev) => {
        const allowed = new Set(nextModelOptions.map((o) => o.value));
        const next = (prev || []).filter((o) => allowed.has(o.value));
        return next;
      });

      dispatch(thunks?.changeMake(remainingMakeVals));
    }

    // For motorcycles, just update makes and clear models if none selected
    if (stateCategory === 'motorcycles') {
      dispatch(thunks?.changeMake(updatedMakes.map((o) => o.value)));
      if (updatedMakes.length === 0) {
        setModelOptions([]);
        setSelectedModel([]);
        dispatch(thunks?.changeModel(''));
      } else {
        const last = updatedMakes[updatedMakes.length - 1];
        const motoModels = last
          ? motorMakeOptions.find((m) => m.label === last.value)?.model || []
          : [];
        setModelOptions(motoModels);
        setSelectedModel([]);
        dispatch(thunks?.changeModel(''));
      }
    }
  };

  const handleMakeChange = (options) => {
    const optsArr = normalizeToArray(options);
    setSelectedMake(optsArr);

    // Dispatch makes as array (adjust if your slice expects otherwise)
    dispatch(thunks?.changeMake(optsArr.map((o) => o.value)));

    // Cars/Classic: union of models across all selected makes
    if (stateCategory === 'cars' || stateCategory === 'classicCollectorCars') {
      const nextModelOptions = getModelOptions(optsArr.map((o) => o.value));
      setModelOptions(nextModelOptions);
      setSelectedModel((prev) => {
        if (!prev || prev.length === 0) return [];
        const allowed = new Set(nextModelOptions.map((o) => o.value));
        const next = prev.filter((o) => allowed.has(o.value));
        return next;
      });
      if (nextModelOptions.length === 0) {
        dispatch(thunks?.changeModel(''));
      }
    }

    // Motorcycles: keep legacy behavior = models from the last selected make
    if (stateCategory === 'motorcycles') {
      const last = optsArr[optsArr.length - 1];
      const motoModels = last
        ? motorMakeOptions.find((m) => m.label === last.value)?.model || []
        : [];
      setModelOptions(motoModels);
      setSelectedModel([]);
      dispatch(thunks?.changeModel(''));
    }
  };

  const handleModelChange = (options) => {
    const optsArr = normalizeToArray(options);
    setSelectedModel(optsArr);

    // Dispatch last-only (keep current behavior). If you want arrays, change this.
    const last = optsArr[optsArr.length - 1];
    if (last) {
      dispatch(thunks?.changeModel(last.value || last.label));
    } else {
      dispatch(thunks?.changeModel(''));
    }
  };

  const handleRemoveModel = (removedOption) => {
    const updatedModel = (selectedModel || []).filter(
      (option) => option.value !== removedOption.value
    );
    setSelectedModel(updatedModel);
    const last = updatedModel[updatedModel.length - 1];
    dispatch(thunks?.changeModel(last ? last.value || last.label : ''));
  };

  // PRICE
  const { min: priceMin, max: priceMax } = useSelector(
    (state) => state[stateCategory].filters.price_range
  );
  const [priceVal, setPriceVal] = useState([priceMin, priceMax]);
  const [togglePrice, handleTogglePrice] = useToggle();
  const handlePriceChange = (event, newValue) => {
    setPriceVal(newValue);
    dispatch(thunks?.changePrice({ min: newValue[0], max: newValue[1] }));
  };
  const handleMinPriceChange = (e) => {
    const newMin = Number(e.target.value);
    setPriceVal([newMin, priceVal[1]]);
    dispatch(thunks?.changePrice({ min: newMin, max: priceVal[1] }));
  };
  const handleMaxPriceChange = (e) => {
    const newMax = Number(e.target.value);
    setPriceVal([priceVal[0], newMax]);
    dispatch(thunks?.changePrice({ min: priceVal[0], max: newMax }));
  };

  // YEAR
  const { min: yearMin, max: yearMax } = useSelector(
    (state) => state[stateCategory].filters.year_range
  );
  const [yearVal, setYearVal] = useState([yearMin, yearMax]);
  const [toggleYear, handleToggleYear] = useToggle();
  const handleYearChange = (event, newValue) => {
    setYearVal(newValue);
    dispatch(thunks?.changeYear({ min: newValue[0], max: newValue[1] }));
  };
  const handleMinYearChange = (e) => {
    const newMin = Number(e.target.value);
    setYearVal([newMin, yearVal[1]]);
    dispatch(thunks?.changeYear({ min: newMin, max: yearVal[1] }));
  };
  const handleMaxYearChange = (e) => {
    const newMax = Number(e.target.value);
    setYearVal([yearVal[0], newMax]);
    dispatch(thunks?.changeYear({ min: yearVal[0], max: newMax }));
  };

  // MILES
  const { min: milesMin, max: milesMax } = useSelector(
    (state) => state[stateCategory].filters.mile_range
  );
  const [milesVal, setMilesVal] = useState([milesMin, milesMax]);
  const [toggleMiles, handleToggleMiles] = useToggle();
  const handleMilesChange = (event, newValue) => {
    setMilesVal(newValue);
    dispatch(thunks?.changeMile({ min: newValue[0], max: newValue[1] }));
  };

  // HOURS
  const { min: hoursMin, max: hoursMax } = useSelector(
    (state) => state[stateCategory].filters.hour_range
  );
  const [hoursVal, setHoursVal] = useState([hoursMin, hoursMax]);
  const [toggleHours, handleToggleHours] = useToggle();
  const handleHoursChange = (event, newValue) => {
    setHoursVal(newValue);
    dispatch(thunks?.changeHours({ min: newValue[0], max: newValue[1] }));
  };

  // HORSEPOWER
  const { min: horsepowerMin, max: horsepowerMax } = useSelector(
    (state) => state[stateCategory].filters.horsepower_range
  );
  const [horsepowerVal, setHorsepowerVal] = useState([
    horsepowerMin,
    horsepowerMax,
  ]);
  const [toggleHorsepower, handleToggleHorsepower] = useToggle();
  const handleHorsepowerChange = (event, newValue) => {
    setHorsepowerVal(newValue);
    dispatch(thunks?.changeHorsepower({ min: newValue[0], max: newValue[1] }));
  };

  // ENGINE SIZE
  const { min: engineSizeMin, max: engineSizeMax } = useSelector(
    (state) => state[stateCategory].filters.sizeEngine_range
  );
  const [engineSizeVal, setEngineSizeVal] = useState([
    engineSizeMin,
    engineSizeMax,
  ]);
  const [toggleEngineSize, handleToggleEngineSize] = useToggle();
  const handleEngineSizeChange = (event, newValue) => {
    setEngineSizeVal(newValue);
    dispatch(thunks?.changeSizeEngine({ min: newValue[0], max: newValue[1] }));
  };

  // LENGTH
  const { min: lengthMin, max: lengthMax } = useSelector(
    (state) => state[stateCategory].filters.length_range
  );
  const [lengthVal, setLengthVal] = useState([lengthMin, lengthMax]);
  const [toggleLength, handleToggleLength] = useToggle();
  const handleLengthChange = (event, newValue) => {
    setLengthVal(newValue);
    dispatch(thunks?.changeLength({ min: newValue[0], max: newValue[1] }));
  };

  // CONDITION
  const { condition } = useSelector((state) => state[stateCategory].filters);

  // Exterior Color
  const { extColors } = useSelector((state) => state[stateCategory].filters);
  const [selectedExtColors, setSelectedExtColors] = useState([]);
  const [toggleExtColor, handleToggleExtColor] = useToggle();
  const handleExtColorChange = (options) => {
    setSelectedExtColors(options ? options.map((option) => option.value) : []);
    dispatch(
      thunks?.changeExtColors(
        options ? options.map((option) => option.value) : []
      )
    );
  };

  // Interior Color
  const { intColors } = useSelector((state) => state[stateCategory].filters);
  const [selectedIntColors, setSelectedIntColors] = useState([]);
  const [toggleIntColor, handleToggleIntColor] = useToggle();
  const handleIntColorChange = (options) => {
    setSelectedIntColors(options ? options.map((option) => option.value) : []);
    dispatch(
      thunks?.changeIntColors(
        options ? options.map((option) => option.value) : []
      )
    );
  };

  // Transmission
  const { transmission } = useSelector((state) => state[stateCategory].filters);
  const [selectedTrans, setSelectedTrans] = useState([]);
  const [toggleTrans, handleToggleTrans] = useToggle();
  const handleTransChange = (options) => {
    setSelectedTrans(options ? options.map((option) => option.value) : []);
    dispatch(
      thunks?.changeTrans(options ? options.map((option) => option.value) : [])
    );
  };

  // Drive
  const { drive } = useSelector((state) => state[stateCategory].filters);
  const [selectedDrive, setSelectedDrive] = useState([]);
  const [toggleDrive, handleToggleDrive] = useToggle();
  const handleDriveChange = (options) => {
    setSelectedDrive(options ? options.map((option) => option.value) : []);
    dispatch(
      thunks?.changeDrive(options ? options.map((option) => option.value) : [])
    );
  };

  // Engine
  const { engine } = useSelector((state) => state[stateCategory].filters);
  const [selectedEngine, setSelectedEngine] = useState([]);
  const [toggleEngine, handleToggleEngine] = useToggle();
  const handleEngineChange = (options) => {
    setSelectedEngine(options ? options.map((option) => option.value) : []);
    dispatch(
      thunks?.changeEngine(options ? options.map((option) => option.value) : [])
    );
  };

  // Body
  const { body } = useSelector((state) => state[stateCategory].filters);
  const [selectedBody, setSelectedBody] = useState([]);
  const [toggleBody, handleToggleBody] = useToggle();
  const handleBodyChange = (options) => {
    setSelectedBody(options ? options.map((option) => option.value) : []);
    dispatch(
      thunks?.changeBody(options ? options.map((option) => option.value) : [])
    );
  };

  // FUEL
  const { fuel } = useSelector((state) => state[stateCategory].filters);
  const [selectedFuel, setSelectedFuel] = useState([]);
  const [toggleFuel, handleToggleFuel] = useToggle();
  const handleFuelChange = (options) => {
    setSelectedFuel(options ? options.map((option) => option.value) : []);
    dispatch(
      thunks?.changeFuel(options ? options.map((option) => option.value) : [])
    );
  };

  // PREMIUM FEATURES
  const { premium_features } = useSelector(
    (state) => state[stateCategory].filters
  );
  const [selectedPremFeatures, setSelectedPremFeatures] = useState([]);
  const [toggleFeatures, handleToggleFeatures] = useToggle();
  const handlePremFeaturesChange = (options) => {
    setSelectedPremFeatures(
      options ? options.map((option) => option.value) : []
    );
    dispatch(
      thunks?.changePremiumFeatures(
        options ? options.map((option) => option.value) : []
      )
    );
  };

  // SELLER
  const { seller } = useSelector((state) => state[stateCategory].filters);

  // TITLE STATUS
  const { cleanTitle } = useSelector((state) => state[stateCategory].filters);

  useEffect(() => {
    const loadThunks = async () => {
      let importedThunks;
      if (stateCategory === 'cars') {
        importedThunks = await import('../store/slices/carsTrucks.slice');
      } else if (stateCategory === 'atvsUtvs') {
        importedThunks = await import('../store/slices/atvsUtvs.slice');
      } else if (stateCategory === 'snowmobiles') {
        importedThunks = await import('../store/slices/snowmobiles.slice');
      } else if (stateCategory === 'watercrafts') {
        importedThunks = await import('../store/slices/watercrafts.slice');
      } else if (stateCategory === 'classicCollectorCars') {
        importedThunks = await import(
          '../store/slices/classicCollectorCars.slice'
        );
      } else if (stateCategory === 'motorcycles') {
        importedThunks = await import('../store/slices/motorcycles.slice');
      } else if (stateCategory === 'rvs') {
        importedThunks = await import('../store/slices/rvs.slice');
      } else if (stateCategory === 'heavyEquipments') {
        importedThunks = await import('../store/slices/heavyEquipments.slice');
      }
      setThunks(importedThunks);
    };

    loadThunks();
  }, [stateCategory]);

  if (!thunks) {
    return <div>Loading...</div>;
  }

  const handleSubmit = () => {
    let finalLat = '';
    let finalLng = '';
    if ((newLat != '') & (newLng != '')) {
      finalLat = newLat;
      finalLng = newLng;
      dispatch(thunks?.changeLocation({ lat: finalLat, lng: finalLng }));
    } else {
      finalLat = lat;
      finalLng = lng;
    }

    onFilter({
      finalLat,
      finalLng,
      radius,
      make,
      model,
      priceVal,
      yearVal,
      milesVal,
      extColors,
      intColors,
      transmission,
      drive,
      engine,
      body,
      fuel,
      premium_features,
      seller,
      cleanTitle,
    });
    dispatch(thunks?.activateFilter(true));
    dispatch(thunks?.setSearchTerm(''));
    dispatch(thunks?.setSortBy('Closest to Me'));
    dispatch(thunks?.resetSortedData());
  };

  const updateLoc = (lat, lng) => {
    setNewLat(lat);
    setNewLng(lng);
  };

  const handleResetFilter = () => {
    window.location.reload();
  };

  const handleShowAdvFilter = () => {
    setShowAdvFilter(!showAdvFilter);
  };

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      border: 'none',
      fontSize: '14px',
      boxShadow: state.isFocused ? 'none' : provided.boxShadow,
      '&:hover': {
        borderColor: 'none',
      },
      cursor: 'pointer',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: () => ({
      display: 'none',
    }),
    input: () => ({
      display: 'none',
    }),
  };

  return (
    <div className='sidebar mobile hide-desktop'>
      <Gmap onChangeLoc={updateLoc} data={data} />

      {filters.includes('Make') && (
        <div className='form-control-wrap'>
          <div className='label-2-cols'>
            <label>Make</label>
            <span>
              {!toggleMake && (
                <Select
                  options={
                    stateCategory === 'motorcycles'
                      ? motorMakeOptions
                      : makeOptionsFromJson
                  }
                  onChange={handleMakeChange}
                  value={selectedMake}
                  placeholder='Select'
                  styles={selectStyles}
                  menuIsOpen={false}
                  className='border-0'
                  isMulti
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  components={{
                    MultiValueRemove: (props) => (
                      <components.MultiValueRemove
                        {...props}
                        onClick={() => handleRemoveOption(props.data)}
                      />
                    ),
                  }}
                />
              )}
            </span>
            <button className='btn-toggle' onClick={handleToggleMake}>
              {toggleMake ? 'Hide Filter' : 'Edit Filter'}
            </button>
          </div>
          {toggleMake && (
            <Select
              options={
                stateCategory === 'motorcycles'
                  ? motorMakeOptions
                  : makeOptionsFromJson
              }
              onChange={handleMakeChange}
              value={selectedMake}
              className='border-0'
              styles={{ marginBottom: '10px' }}
              isMulti
            />
          )}
        </div>
      )}

      {filters.includes('Model') && (
        <div className='form-control-wrap'>
          <div className='label-2-cols'>
            <label>Model</label>
            <span>
              {!toggleModel && (
                <Select
                  options={modelOptions}
                  onChange={handleModelChange}
                  value={selectedModel}
                  placeholder='Select'
                  isDisabled={modelOptions.length <= 0}
                  styles={selectStyles}
                  menuIsOpen={false}
                  className='border-0'
                  isMulti
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  components={{
                    MultiValueRemove: (props) => (
                      <components.MultiValueRemove
                        {...props}
                        onClick={() => handleRemoveModel(props.data)}
                      />
                    ),
                  }}
                />
              )}
            </span>
            <button className='btn-toggle' onClick={handleToggleModel}>
              {toggleModel ? 'Hide Filter' : 'Edit Filter'}
            </button>
          </div>
          {toggleModel && (
            <Select
              options={modelOptions}
              onChange={handleModelChange}
              value={selectedModel}
              isDisabled={modelOptions.length <= 0}
              className='border-0'
              isMulti
            />
          )}
        </div>
      )}

      {filters.includes('Price') && (
        <div className='form-control-wrap'>
          <div className='label-2-cols'>
            <label>Price</label>
            <span>
              {!togglePrice && (
                <div className='price-range-wrap'>
                  <span>${priceVal[0].toLocaleString('en-US')} - </span>
                  <span>${priceVal[1].toLocaleString('en-US')}</span>
                </div>
              )}
            </span>
            <button className='btn-toggle' onClick={handleTogglePrice}>
              {togglePrice ? 'Hide Filter' : 'Edit Filter'}
            </button>
          </div>
          {togglePrice && (
            <>
              <div className='range-inputs'>
                <input
                  type='number'
                  value={priceVal[0]}
                  min={0}
                  onChange={handleMinPriceChange}
                  placeholder='Min'
                />
                <span className='price-separator'>–</span>
                <input
                  type='number'
                  value={priceVal[1]}
                  min={0}
                  onChange={handleMaxPriceChange}
                  placeholder='Max'
                />
              </div>
            </>
          )}
        </div>
      )}

      {filters.includes('Year') && (
        <div className='form-control-wrap'>
          <div className='label-2-cols'>
            <label>Year</label>
            <span>
              {!toggleYear && (
                <div className='price-range-wrap'>
                  <span>{yearVal[0]} - </span>
                  <span>{yearVal[1]}</span>
                </div>
              )}
            </span>
            <button className='btn-toggle' onClick={handleToggleYear}>
              {toggleYear ? 'Hide Filter' : 'Edit Filter'}
            </button>
          </div>
          {toggleYear && (
            <>
              <div className='range-inputs'>
                <input
                  type='number'
                  value={yearVal[0]}
                  min={1900}
                  onChange={handleMinYearChange}
                  placeholder='Min'
                />
                <span className='range-separator'>–</span>
                <input
                  type='number'
                  value={yearVal[1]}
                  max={2025}
                  onChange={handleMaxYearChange}
                  placeholder='Max'
                />
              </div>
            </>
          )}
        </div>
      )}

      {filters.includes('Year Classic') && (
        <div className='form-control-wrap'>
          <div className='label-2-cols'>
            <label>Year</label>
            <span>
              {!toggleYear && (
                <div className='price-range-wrap'>
                  <span>{yearVal[0]} - </span>
                  <span>{yearVal[1]}</span>
                </div>
              )}
            </span>
            <button className='btn-toggle' onClick={handleToggleYear}>
              {toggleYear ? 'Hide Filter' : 'Edit Filter'}
            </button>
          </div>
          {toggleYear && (
            <>
              <Slider
                value={yearVal}
                onChange={handleYearChange}
                valueLabelDisplay='auto'
                min={1990}
                max={2025}
                step={1}
              />
              <div className='price-range-wrap'>
                <span>{yearVal[0]} - </span>
                <span>{yearVal[1]}</span>
              </div>
            </>
          )}
        </div>
      )}

      {filters.includes('Mileage') && (
        <div className='form-control-wrap'>
          <div className='label-2-cols'>
            <label>Mileage</label>
            <span>
              {!toggleMiles && (
                <div className='price-range-wrap'>
                  <span>{milesVal[0].toLocaleString('en-US')} - </span>
                  <span>{milesVal[1].toLocaleString('en-US')}</span>
                </div>
              )}
            </span>
            <button className='btn-toggle' onClick={handleToggleMiles}>
              {toggleMiles ? 'Hide Filter' : 'Edit Filter'}
            </button>
          </div>
          {toggleMiles && (
            <>
              <div className='range-inputs'>
                <input
                  type='number'
                  value={milesVal[0]}
                  min={0}
                  onChange={(e) => {
                    const newMin = Number(e.target.value);
                    setMilesVal([newMin, milesVal[1]]);
                    dispatch(
                      thunks?.changeMile({ min: newMin, max: milesVal[1] })
                    );
                  }}
                  placeholder='Min'
                />
                <span className='range-separator'>–</span>
                <input
                  type='number'
                  value={milesVal[1]}
                  min={milesVal[0]}
                  onChange={(e) => {
                    const newMax = Number(e.target.value);
                    setMilesVal([milesVal[0], newMax]);
                    dispatch(
                      thunks?.changeMile({ min: milesVal[0], max: newMax })
                    );
                  }}
                  placeholder='Max'
                />
              </div>
            </>
          )}
        </div>
      )}

      {filters.includes('Hours') && (
        <div className='form-control-wrap'>
          <div className='label-2-cols'>
            <label>Hours</label>
            <span>
              {!toggleHours && (
                <div className='price-range-wrap'>
                  <span>{hoursVal[0].toLocaleString('en-US')} - </span>
                  <span>{hoursVal[1].toLocaleString('en-US')}</span>
                </div>
              )}
            </span>
            <button className='btn-toggle' onClick={handleToggleHours}>
              {toggleHours ? 'Hide Filter' : 'Edit Filter'}
            </button>
          </div>
          {toggleHours && (
            <>
              <Slider
                value={hoursVal}
                onChange={handleHoursChange}
                valueLabelDisplay='auto'
                max={100}
                step={1}
              />
              <div className='price-range-wrap'>
                <span>{hoursVal[0].toLocaleString('en-US')} - </span>
                <span>{hoursVal[1].toLocaleString('en-US')}</span>
              </div>
            </>
          )}
        </div>
      )}

      <div id='advanceFilter'>
        <div className='header' onClick={handleShowAdvFilter}>
          <span>Additional Search Filters</span>
          <span className='action-button'>
            {showAdvFilter ? (
              <FaMinusCircle size={20} />
            ) : (
              <FaPlusCircle size={20} />
            )}
          </span>
        </div>

        {showAdvFilter && (
          <div className='advance-filters-wrap'>
            {filters.includes('Condition') && (
              <div className='form-control-wrap'>
                <label>Condition</label>
                <FormControlLabel
                  control={<Switch />}
                  label='New'
                  onChange={() => {
                    if (condition == null) {
                      dispatch(thunks?.changeCondition(true));
                      return;
                    } else {
                      dispatch(thunks?.changeCondition(!condition));
                      return;
                    }
                  }}
                />
              </div>
            )}

            {filters.includes('Horsepower') && (
              <div className='form-control-wrap'>
                <div className='label-2-cols'>
                  <label>Horsepower</label>
                  <span>
                    {!toggleHorsepower && (
                      <div className='price-range-wrap'>
                        <span>{horsepowerVal[0]} - </span>
                        <span>{horsepowerVal[1]}</span>
                      </div>
                    )}
                  </span>
                  <button
                    className='btn-toggle'
                    onClick={handleToggleHorsepower}
                  >
                    {toggleHorsepower ? 'Hide Filter' : 'Edit Filter'}
                  </button>
                </div>
                {toggleHorsepower && (
                  <>
                    <Slider
                      value={horsepowerVal}
                      onChange={handleHorsepowerChange}
                      valueLabelDisplay='auto'
                      min={0}
                      max={200}
                      step={1}
                    />
                    <div className='price-range-wrap'>
                      <span>{horsepowerVal[0]} - </span>
                      <span>{horsepowerVal[1]}</span>
                    </div>
                  </>
                )}
              </div>
            )}

            {filters.includes('Engine Size') && (
              <div className='form-control-wrap'>
                <div className='label-2-cols'>
                  <label>Engine Size</label>
                  <span>
                    {!toggleEngineSize && (
                      <div className='price-range-wrap'>
                        <span>{engineSizeVal[0]} ccs - </span>
                        <span>{engineSizeVal[1]} ccs</span>
                      </div>
                    )}
                  </span>
                  <button
                    className='btn-toggle'
                    onClick={handleToggleEngineSize}
                  >
                    {toggleEngineSize ? 'Hide Filter' : 'Edit Filter'}
                  </button>
                </div>
                {toggleEngineSize && (
                  <>
                    <Slider
                      value={engineSizeVal}
                      onChange={handleEngineSizeChange}
                      valueLabelDisplay='auto'
                      min={0}
                      max={1000}
                      step={50}
                    />
                    <div className='price-range-wrap'>
                      <span>{engineSizeVal[0]} - </span>
                      <span>{engineSizeVal[1]}</span>
                    </div>
                  </>
                )}
              </div>
            )}

            {filters.includes('Length') && (
              <div className='form-control-wrap'>
                <div className='label-2-cols'>
                  <label>Length</label>
                  <span>
                    {!toggleLength && (
                      <div className='price-range-wrap'>
                        <span>{lengthVal[0]} ft - </span>
                        <span>{lengthVal[1]} ft</span>
                      </div>
                    )}
                  </span>
                  <button className='btn-toggle' onClick={handleToggleLength}>
                    {toggleLength ? 'Hide Filter' : 'Edit Filter'}
                  </button>
                </div>
                {toggleLength && (
                  <>
                    <Slider
                      value={lengthVal}
                      onChange={handleLengthChange}
                      valueLabelDisplay='auto'
                      min={0}
                      max={500}
                      step={1}
                    />
                    <div className='price-range-wrap'>
                      <span>{lengthVal[0]} - </span>
                      <span>{lengthVal[1]}</span>
                    </div>
                  </>
                )}
              </div>
            )}

            {filters.includes('Exterior Color') && (
              <div className='form-control-wrap'>
                <div className='label-2-cols'>
                  <label>Exterior Color</label>
                  <span>
                    {!toggleExtColor && (
                      <Select
                        options={colorOptions}
                        onChange={handleExtColorChange}
                        value={colorOptions.filter((option) =>
                          selectedExtColors.includes(option.value)
                        )}
                        placeholder='Select'
                        styles={selectStyles}
                        menuIsOpen={false}
                        className='border-0'
                        isMulti
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        components={{
                          MultiValueRemove: (props) => (
                            <components.MultiValueRemove
                              {...props}
                              onClick={() => handleRemoveOption(props.data)}
                            />
                          ),
                        }}
                      />
                    )}
                  </span>
                  <button className='btn-toggle' onClick={handleToggleExtColor}>
                    {toggleExtColor ? 'Hide Filter' : 'Edit Filter'}
                  </button>
                </div>
                {toggleExtColor && (
                  <Select
                    isMulti
                    options={colorOptions}
                    onChange={handleExtColorChange}
                    value={colorOptions.filter((option) =>
                      selectedExtColors.includes(option.value)
                    )}
                    className='border-0'
                  />
                )}
              </div>
            )}

            {filters.includes('Interior Color') && (
              <div className='form-control-wrap'>
                <div className='label-2-cols'>
                  <label>Interior Color</label>
                  <span>
                    {!toggleIntColor && (
                      <Select
                        options={colorOptions}
                        onChange={handleIntColorChange}
                        value={colorOptions.filter((option) =>
                          selectedIntColors.includes(option.value)
                        )}
                        placeholder='Select'
                        styles={selectStyles}
                        menuIsOpen={false}
                        className='border-0'
                        isMulti
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        components={{
                          MultiValueRemove: (props) => (
                            <components.MultiValueRemove
                              {...props}
                              onClick={() => handleRemoveOption(props.data)}
                            />
                          ),
                        }}
                      />
                    )}
                  </span>
                  <button className='btn-toggle' onClick={handleToggleIntColor}>
                    {toggleIntColor ? 'Hide Filter' : 'Edit Filter'}
                  </button>
                </div>
                {toggleIntColor && (
                  <Select
                    isMulti
                    options={colorOptions}
                    onChange={handleIntColorChange}
                    value={colorOptions.filter((option) =>
                      selectedIntColors.includes(option.value)
                    )}
                    className='border-0'
                  />
                )}
              </div>
            )}

            {filters.includes('Transmission') && (
              <div className='form-control-wrap'>
                <div className='label-2-cols'>
                  <label>Transmission</label>
                  <span>
                    {!toggleTrans && (
                      <Select
                        options={transOptions}
                        onChange={handleTransChange}
                        value={transOptions.filter((option) =>
                          selectedTrans.includes(option.value)
                        )}
                        placeholder='Select'
                        styles={selectStyles}
                        menuIsOpen={false}
                        className='border-0'
                        isMulti
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        components={{
                          MultiValueRemove: (props) => (
                            <components.MultiValueRemove
                              {...props}
                              onClick={() => handleRemoveOption(props.data)}
                            />
                          ),
                        }}
                      />
                    )}
                  </span>
                  <button className='btn-toggle' onClick={handleToggleTrans}>
                    {toggleTrans ? 'Hide Filter' : 'Edit Filter'}
                  </button>
                </div>
                {toggleTrans && (
                  <Select
                    isMulti
                    options={transOptions}
                    onChange={handleTransChange}
                    value={transOptions.filter((option) =>
                      selectedTrans.includes(option.value)
                    )}
                    className='border-0'
                  />
                )}
              </div>
            )}

            {filters.includes('Drive Type') && (
              <div className='form-control-wrap'>
                <div className='label-2-cols'>
                  <label>Drive Type</label>
                  <span>
                    {!toggleDrive && (
                      <Select
                        isMulti
                        options={driveOptions}
                        onChange={handleDriveChange}
                        value={driveOptions.filter((option) =>
                          selectedDrive.includes(option.value)
                        )}
                        placeholder='Select'
                        styles={selectStyles}
                        menuIsOpen={false}
                        className='border-0'
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        components={{
                          MultiValueRemove: (props) => (
                            <components.MultiValueRemove
                              {...props}
                              onClick={() => handleRemoveOption(props.data)}
                            />
                          ),
                        }}
                      />
                    )}
                  </span>
                  <button className='btn-toggle' onClick={handleToggleDrive}>
                    {toggleDrive ? 'Hide Filter' : 'Edit Filter'}
                  </button>
                </div>
                {toggleDrive && (
                  <Select
                    isMulti
                    options={driveOptions}
                    onChange={handleDriveChange}
                    value={driveOptions.filter((option) =>
                      selectedDrive.includes(option.value)
                    )}
                    className='border-0'
                  />
                )}
              </div>
            )}

            {filters.includes('Engine Type') && (
              <div className='form-control-wrap'>
                <div className='label-2-cols'>
                  <label>Engine Type</label>
                  <span>
                    {!toggleEngine && (
                      <Select
                        isMulti
                        options={engineOptions}
                        onChange={handleEngineChange}
                        value={engineOptions.filter((option) =>
                          selectedEngine.includes(option.value)
                        )}
                        placeholder='Select'
                        styles={selectStyles}
                        menuIsOpen={false}
                        className='border-0'
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        components={{
                          MultiValueRemove: (props) => (
                            <components.MultiValueRemove
                              {...props}
                              onClick={() => handleRemoveOption(props.data)}
                            />
                          ),
                        }}
                      />
                    )}
                  </span>
                  <button className='btn-toggle' onClick={handleToggleEngine}>
                    {toggleEngine ? 'Hide Filter' : 'Edit Filter'}
                  </button>
                </div>
                {toggleEngine && (
                  <Select
                    isMulti
                    options={engineOptions}
                    onChange={handleEngineChange}
                    value={engineOptions.filter((option) =>
                      selectedEngine.includes(option.value)
                    )}
                    className='border-0'
                  />
                )}
              </div>
            )}

            {filters.includes('Body Style') && (
              <div className='form-control-wrap'>
                <div className='label-2-cols'>
                  <label>Body Style</label>
                  <span>
                    {!toggleBody && (
                      <Select
                        isMulti
                        options={bodyOptions}
                        onChange={handleBodyChange}
                        value={bodyOptions.filter((option) =>
                          selectedBody.includes(option.value)
                        )}
                        placeholder='Select'
                        styles={selectStyles}
                        menuIsOpen={false}
                        className='border-0'
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        components={{
                          MultiValueRemove: (props) => (
                            <components.MultiValueRemove
                              {...props}
                              onClick={() => handleRemoveOption(props.data)}
                            />
                          ),
                        }}
                      />
                    )}
                  </span>
                  <button className='btn-toggle' onClick={handleToggleBody}>
                    {toggleBody ? 'Hide Filter' : 'Edit Filter'}
                  </button>
                </div>
                {toggleBody && (
                  <Select
                    isMulti
                    options={bodyOptions}
                    onChange={handleBodyChange}
                    value={bodyOptions.filter((option) =>
                      selectedBody.includes(option.value)
                    )}
                    className='border-0'
                  />
                )}
              </div>
            )}

            {filters.includes('Fuel Type') && (
              <div className='form-control-wrap'>
                <div className='label-2-cols'>
                  <label>Fuel Type</label>
                  <span>
                    {!toggleFuel && (
                      <Select
                        isMulti
                        options={fuelOptions}
                        onChange={handleFuelChange}
                        value={fuelOptions.filter((option) =>
                          selectedFuel.includes(option.value)
                        )}
                        placeholder='Select'
                        styles={selectStyles}
                        menuIsOpen={false}
                        className='border-0'
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        components={{
                          MultiValueRemove: (props) => (
                            <components.MultiValueRemove
                              {...props}
                              onClick={() => handleRemoveOption(props.data)}
                            />
                          ),
                        }}
                      />
                    )}
                  </span>
                  <button className='btn-toggle' onClick={handleToggleFuel}>
                    {toggleFuel ? 'Hide Filter' : 'Edit Filter'}
                  </button>
                </div>
                {toggleFuel && (
                  <Select
                    isMulti
                    options={fuelOptions}
                    onChange={handleFuelChange}
                    value={fuelOptions.filter((option) =>
                      selectedFuel.includes(option.value)
                    )}
                    className='border-0'
                  />
                )}
              </div>
            )}

            {filters.includes('Premium Features') && (
              <div className='form-control-wrap'>
                <div className='label-2-cols'>
                  <label style={{ lineHeight: '14px' }}>Premium Features</label>
                  <span>
                    {!toggleFeatures && (
                      <Select
                        isMulti
                        options={premiumFeaturesOptions}
                        onChange={handlePremFeaturesChange}
                        value={premiumFeaturesOptions.filter((option) =>
                          selectedPremFeatures.includes(option.value)
                        )}
                        placeholder='Select'
                        styles={selectStyles}
                        menuIsOpen={false}
                        className='border-0'
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        components={{
                          MultiValueRemove: (props) => (
                            <components.MultiValueRemove
                              {...props}
                              onClick={() => handleRemoveOption(props.data)}
                            />
                          ),
                        }}
                      />
                    )}
                  </span>
                  <button className='btn-toggle' onClick={handleToggleFeatures}>
                    {toggleFeatures ? 'Hide Filter' : 'Edit Filter'}
                  </button>
                </div>
                {toggleFeatures && (
                  <Select
                    isMulti
                    options={premiumFeaturesOptions}
                    onChange={handlePremFeaturesChange}
                    value={premiumFeaturesOptions.filter((option) =>
                      selectedPremFeatures.includes(option.value)
                    )}
                    className='border-0'
                  />
                )}
              </div>
            )}

            {filters.includes('Seller Type') && (
              <div className='form-control-wrap' style={{ marginTop: '15px' }}>
                <label>Seller Type</label>
                <FormControlLabel
                  control={<Switch />}
                  label='Individuals Only'
                  onChange={() => {
                    if (seller == null) {
                      dispatch(thunks?.changeSeller(true));
                      return;
                    } else {
                      dispatch(thunks?.changeSeller(!seller));
                      return;
                    }
                  }}
                />
              </div>
            )}

            {filters.includes('Title Status') && (
              <div className='form-control-wrap'>
                <label>Title Status</label>
                <FormControlLabel
                  control={<Switch />}
                  label='Clean Titles'
                  onChange={() => {
                    if (cleanTitle == null) {
                      dispatch(thunks?.changeCleanTitle(true));
                      return;
                    } else {
                      dispatch(thunks?.changeCleanTitle(!cleanTitle));
                      return;
                    }
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <button onClick={() => handleSubmit()} className='primary mb-10'>
        Filter
      </button>
      <button
        onClick={() => handleResetFilter()}
        className='reset'
        disabled={!isFilter}
      >
        Reset Filter
      </button>
    </div>
  );
};

export default FiltersMobile;
